import { FC } from 'react';

import { format } from 'date-fns';
import { omit } from 'ramda';
import { useFormContext } from 'react-hook-form';

import { Label } from 'components/shared/Label';
import { EFlightStatus } from 'detailsv2/structure';
import { formatRepresentationDate, getLocalDateFromString, isIDL } from 'utils/date';

import { getUniqueFlightAPIKey } from '../../../../../../../../../GuestPortal/Flights/AddFlights/utils';
import { getMinutesAndHoursFormatV2 } from '../../../functions';
import s from './index.module.css';
import { IFlightAPIOptionProps, ISelectButtonProps, TFlightApiOptionData } from './types';

export const FlightAPIOption: FC<Omit<IFlightAPIOptionProps, 'newDepartureTime' | 'newArrivalTime'>> = ({
  selectedOptions,
  setSelectedOptions,
  flightStatus,
  flightDate,
  airline,
  flightNumber,
  departureTime,
  arrivalTime,
  departureAirport,
  arrivalAirport,
  isShowSelect,
}) => {
  const dataKey = getUniqueFlightAPIKey({ flightNumber, departureAirport, arrivalAirport });
  const isOptionSelected = Object.keys(selectedOptions).includes(dataKey);
  const { getValues } = useFormContext();

  return (
    <div className={s.container}>
      <div className={s.flight}>
        <div className={s.flightInfo}>
          <div>
            <span className={s.flightNumber}>
              {airline.name} {airline.fsCode} {flightNumber}
              {'  '}
            </span>
            <span className={s.flightRoute}>
              {departureAirport.fsCode} → {arrivalAirport.fsCode} on{' '}
              {format(getLocalDateFromString(flightDate), 'EEE, MMM d')}
            </span>
          </div>
        </div>
        {isShowSelect ? (
          <SelectButton
            className={s.actionButton}
            isSelected={isOptionSelected}
            onClick={() => {
              if (isOptionSelected) {
                setSelectedOptions((prev) => omit([dataKey], prev));
              } else {
                const currentDay = getValues('daysRange.start');
                const option = createSelectedOption({
                  departureAirport,
                  arrivalAirport,
                  arrivalTime,
                  departureTime,
                  airline,
                  flightNumber,
                  currentDay,
                });
                setSelectedOptions((prev) => ({ ...prev, [dataKey]: option }));
              }
            }}
          />
        ) : (
          flightStatus !== EFlightStatus.Common && (
            <Label color="#CE4622" text="Flight Change" className={s.flightChangeLabel} />
          )
        )}
      </div>
      <div className={s.details}>
        <div className={s.departure}>
          <p>
            Estimated Departure Time:
            <b> {getMinutesAndHoursFormatV2(new Date(departureTime), departureAirport.utcOffsetHours)}</b>
          </p>
          <p>
            Departure Airport: <b>{departureAirport.name}</b>
          </p>
          <p>
            Departure City: <b>{departureAirport.city}</b>
          </p>
        </div>
        <div className={s.detailsDivider} />
        <div className={s.arrival}>
          <p>
            Estimated Arrival Time:
            <b>{getMinutesAndHoursFormatV2(new Date(arrivalTime), arrivalAirport.utcOffsetHours)}</b>
          </p>
          <p>
            Arrival Airport: <b>{arrivalAirport.name}</b>
          </p>
          <p>
            Arrival City: <b>{arrivalAirport.city}</b>
          </p>
        </div>
      </div>
    </div>
  );
};

const SelectButton = ({ isSelected, onClick, className }: ISelectButtonProps) => {
  return (
    <button type="button" onClick={onClick} className={className}>
      {isSelected ? (
        <>
          <div className={s.check}></div>
          Selected
        </>
      ) : (
        'Select'
      )}
    </button>
  );
};

export const createSelectedOption = ({
  departureAirport,
  arrivalAirport,
  arrivalTime,
  departureTime,
  airline,
  flightNumber,
  currentDay,
}: Omit<TFlightApiOptionData, 'flightDate' | 'flightStatus' | 'cancelled' | 'newArrivalTime' | 'newDepartureTime'> & {
  currentDay: any;
}) => {
  const formattedDepartureDate = formatRepresentationDate(departureTime);
  const formattedArrivalDate = formatRepresentationDate(arrivalTime);

  return {
    data: {
      departureAirport,
      arrivalAirport,
      arrivalTime,
      departureTime,
      airline,
      flightNumber,
      // Reset the status when selecting a new flight
      flightStatus: EFlightStatus.Common,
      newArrivalTime: [],
      newDepartureTime: [],
    },
    datesRange: {
      end: formattedDepartureDate !== formattedArrivalDate ? formattedArrivalDate : null,
    },
    daysRange: {
      end:
        formattedDepartureDate !== formattedArrivalDate
          ? currentDay + isIDL(formattedDepartureDate, formattedArrivalDate)
            ? 1
            : -1
          : null,
    },
  };
};
